import { c as _c } from "react/compiler-runtime";
import type { LineOfCoverageType, PolicyType } from '@newfront-insurance/coverage-api';
import type { NextApiError } from '@newfront-insurance/next-api-error';
import type { ConfigInterface } from '@newfront-insurance/next-api-swr';
import differenceBy from 'lodash/differenceBy';
import { useState, useEffect, useMemo } from 'react';
import { add, remove, calculateOptions } from './helpers';
import type { LineOfCoverageTypeOption } from '../../types';
import { calculateNewOption, typesToOptions } from '../../utils/line-of-coverage-type-option';
import { useDefinitionsHelpers } from '../definitions';
export interface OptionsResult {
  loading: boolean;
  error?: NextApiError;
  options: LineOfCoverageTypeOption[];
  addOption: (type: LineOfCoverageType) => unknown;
  selectOption: (option: LineOfCoverageTypeOption) => unknown;
  unselectOption: (option: LineOfCoverageTypeOption) => unknown;
}
export interface Params {
  policyType: PolicyType;
  selectAllByDefault?: boolean;
  selectedOptions: LineOfCoverageTypeOption[];
  selectOptions: (newSelectedOptions: LineOfCoverageTypeOption[]) => unknown;
}
export function useLineOfCoverageOptions(t0, config) {
  const $ = _c(38);
  const {
    policyType,
    selectAllByDefault,
    selectedOptions,
    selectOptions
  } = t0;
  const [selectAllByDefaultDone, setSelectAllByDefaultDone] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [options, setOptions] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    loading,
    error,
    policyTypeDefinitions
  } = useDefinitionsHelpers(t2, config);
  let t3;
  let t4;
  if ($[2] !== policyType || $[3] !== policyTypeDefinitions) {
    t4 = policyTypeDefinitions[policyType]?.locs ?? [];
    $[2] = policyType;
    $[3] = policyTypeDefinitions;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = typesToOptions(t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  t3 = t5;
  const defaultOptionsForPolicyType = t3;
  let t6;
  let t7;
  if ($[7] !== defaultOptionsForPolicyType || $[8] !== selectedOptions) {
    t6 = () => {
      setOptions(() => calculateOptions(defaultOptionsForPolicyType, selectedOptions));
    };
    t7 = [defaultOptionsForPolicyType, selectedOptions];
    $[7] = defaultOptionsForPolicyType;
    $[8] = selectedOptions;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setSelectAllByDefaultDone(false);
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== policyType) {
    t9 = [policyType];
    $[12] = policyType;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[14] !== options || $[15] !== selectAllByDefault || $[16] !== selectAllByDefaultDone || $[17] !== selectOptions || $[18] !== selectedOptions) {
    t10 = () => {
      if (!selectAllByDefault || selectAllByDefaultDone) {
        return;
      }
      setSelectAllByDefaultDone(true);
      const unselectedOptions = differenceBy(options, selectedOptions, _temp);
      if (unselectedOptions.length > 0) {
        selectOptions(options);
      }
    };
    t11 = [selectAllByDefault, selectAllByDefaultDone, selectOptions, options, selectedOptions];
    $[14] = options;
    $[15] = selectAllByDefault;
    $[16] = selectAllByDefaultDone;
    $[17] = selectOptions;
    $[18] = selectedOptions;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  if ($[21] !== options || $[22] !== selectOptions || $[23] !== selectedOptions) {
    t12 = function addOption(type) {
      const newOption = calculateNewOption(options, type);
      setOptions(add(options, newOption));
      selectOptions(add(selectedOptions, newOption));
    };
    $[21] = options;
    $[22] = selectOptions;
    $[23] = selectedOptions;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const addOption = t12;
  let t13;
  if ($[25] !== selectOptions || $[26] !== selectedOptions) {
    t13 = function selectOption(option) {
      selectOptions(add(selectedOptions, option));
    };
    $[25] = selectOptions;
    $[26] = selectedOptions;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const selectOption = t13;
  let t14;
  if ($[28] !== selectOptions || $[29] !== selectedOptions) {
    t14 = function unselectOption(option_0) {
      selectOptions(remove(selectedOptions, option_0));
    };
    $[28] = selectOptions;
    $[29] = selectedOptions;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const unselectOption = t14;
  let t15;
  if ($[31] !== addOption || $[32] !== error || $[33] !== loading || $[34] !== options || $[35] !== selectOption || $[36] !== unselectOption) {
    t15 = {
      loading,
      error,
      options,
      addOption,
      selectOption,
      unselectOption
    };
    $[31] = addOption;
    $[32] = error;
    $[33] = loading;
    $[34] = options;
    $[35] = selectOption;
    $[36] = unselectOption;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}